'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

const AdminLoginPage = () => {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      router.push('/admin-dashboard');
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <Link href="/homepage" className="inline-block">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="HeartIcon" size={32} className="text-primary" />
            </div>
          </Link>
          <h1 className="font-playfair text-3xl font-semibold text-foreground mb-2">
            Family Dashboard
          </h1>
          <p className="font-inter text-sm text-muted-foreground">
            Sign in to access RSVP management
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-card rounded-lg border border-border shadow-sacred-md p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block font-inter text-sm font-medium text-foreground mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-border rounded-lg font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="admin@wedding.com"
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block font-inter text-sm font-medium text-foreground mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-border rounded-lg font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter your password"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0" />
                <p className="font-inter text-sm text-error">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-inter text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="font-inter text-xs text-muted-foreground text-center mb-3">
              Demo Credentials:
            </p>
            <div className="space-y-2">
              <div className="bg-surface rounded p-3">
                <p className="font-inter text-xs font-medium text-foreground">Admin Access:</p>
                <p className="font-inter text-xs text-muted-foreground mt-1">
                  admin@wedding.com / admin123
                </p>
              </div>
              <div className="bg-surface rounded p-3">
                <p className="font-inter text-xs font-medium text-foreground">Family Access:</p>
                <p className="font-inter text-xs text-muted-foreground mt-1">
                  family@wedding.com / family123
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Website */}
        <div className="text-center mt-6">
          <Link
            href="/homepage"
            className="inline-flex items-center gap-2 font-inter text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;